import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { RegistrationForm } from "../components/RegistrationForm";
import { Countdown } from "../components/Countdown";
import { SeatsLeft } from "../components/SeatsLeft";
import { HERO, HOST, WEBINAR, BONUSES, GREEN, PORTRAIT_URL, scrollToId } from "../lib/content";

const lineAnim = (i) => ({
  initial: { opacity: 0, y: 40, clipPath: "inset(0 0 100% 0)" },
  animate: { opacity: 1, y: 0, clipPath: "inset(0 0 0% 0)" },
  transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
});

export default function Hero() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16 lg:pt-24 lg:pb-0" data-testid="hero-section">
      <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative z-10">
          <div className="font-mono-c text-[10px] tracking-[0.3em] mb-3 text-black/30 hidden md:block">
            📅 {WEBINAR.date}
          </div>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.6 }}
            className="font-mono-c text-[10px] tracking-[0.25em] mb-6" style={{ color: "#6D28D9" }}
            data-testid="hero-eyebrow"
          >
            {HERO.eyebrow}
          </motion.p>
          <h1 className="font-display font-bold uppercase" style={{ fontSize: "clamp(30px, 3.4vw, 62px)", letterSpacing: "-0.03em", lineHeight: 0.95 }} data-testid="hero-h1">
            <motion.span className="block whitespace-nowrap" {...lineAnim(0)}>{HERO.h1a}</motion.span>
            <motion.span className="block whitespace-nowrap" style={{ color: "#7C3AED" }} {...lineAnim(1)}>{HERO.h1b}</motion.span>
            <motion.span className="block opacity-60" style={{ fontSize: "62%" }} {...lineAnim(2)}>{HERO.h1c}</motion.span>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-6 text-[15px] leading-relaxed text-black/55 max-w-lg" data-testid="hero-subheadline">
            The free session that shows <span style={{ color: "#6D28D9" }}>first-time buyers</span> in Nevada and Texas exactly what they qualify for — even if a big bank already told them <span style={{ color: "#6D28D9" }}>not yet</span>.
          </motion.p>
          <motion.blockquote initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-6 pl-4 italic text-sm text-black/60 max-w-md" style={{ borderLeft: "2px solid #7C3AED" }}>
            {HERO.blockquote}
          </motion.blockquote>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.6 }} className="mt-7">
            <p className="font-mono-c text-[10px] tracking-[0.3em] text-black/35 mb-3">LIVE SESSION BEGINS IN</p>
            <div className="flex flex-wrap items-center gap-4">
              <Countdown />
              <SeatsLeft />
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.6 }} className="mt-7">
            <button onClick={() => scrollToId("register")} className="btn-accent px-8 py-4 text-[13px]" data-testid="hero-cta-button">
              ✓ RESERVE MY FREE SEAT
            </button>
            <p className="font-mono-c text-[11px] text-black/25 mt-3 tracking-wide">LOW TICKET NOTICE! — Seats fill fast</p>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 0.7 }}
            className="mt-8 flex items-center gap-4" data-testid="hero-host-module">
            <img src={PORTRAIT_URL} alt={HOST.name} className="w-12 h-12 rounded-full object-cover" style={{ border: "2px solid #7C3AED" }} />
            <div>
              <p className="text-sm font-semibold text-[#181818]">{HOST.name}</p>
              <p className="font-mono-c text-[10px] tracking-[0.15em] text-black/40">{HOST.company} · NMLS# {HOST.nmls}</p>
            </div>
          </motion.div>
        </div>

        <div className="relative lg:min-h-[80vh] flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 glass-panel p-6 md:p-8 w-full max-w-md"
            style={{ background: "rgba(255,255,255,0.85)" }}
            data-testid="hero-registration-card"
          >
            <p className="font-mono-c text-[10px] tracking-[0.3em] mb-1.5" style={{ color: "#6D28D9" }}>DON'T MISS OUT</p>
            <h3 className="font-display font-semibold text-lg uppercase tracking-tight mb-4">When You Register, You'll Receive:</h3>
            <ul className="space-y-2 mb-4">
              {BONUSES.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-[13px] text-black/70">
                  <Check size={15} className="mt-0.5 shrink-0" style={{ color: GREEN }} />
                  {b}
                </li>
              ))}
            </ul>
            <p className="font-display font-bold text-[22px] mb-4" style={{ color: "#7C3AED" }}>FREE!</p>
            <RegistrationForm idPrefix="hero" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
